
import { Member } from '../types/memberTypes';

/**
 * Check if any user in the members list is a Primary Owner
 */
export const isUserPrimaryOwner = (members: Member[]): boolean => {
  return members.some(member => member.role === 'Primary Owner');
};

/**
 * Check if any user in the members list is an Owner
 */
export const isUserOwner = (members: Member[]): boolean => {
  return members.some(member => member.role === 'Owner');
};

/**
 * Check if the current user is a Primary Owner
 */
export const isCurrentUserPrimaryOwner = (members: Member[]): boolean => {
  return members.some(member => member.isCurrentUser && member.role === 'Primary Owner');
};

/**
 * Check if the current user can manage users in the entity
 */
export const canCurrentUserManageUsers = (members: Member[]): boolean => {
  const currentMember = members.find(member => member.isCurrentUser);
  
  if (!currentMember) return false;
  
  // Owners and Primary Owners can always manage users
  if (currentMember.role === 'Primary Owner' || currentMember.role === 'Owner') {
    return true;
  }
  
  // Check for specific permissions
  return currentMember.permissions?.some(p => 
    (p.id === 'portfolioUsers' && p.checked) ||
    (p.id === 'businessUsers' && p.checked)
  ) || false;
};

/**
 * Get permissions by role
 */
export const getPermissionsByRole = (role: string): any[] => {
  const basePermissions = [
    { id: 'viewOnly', name: 'View Only', checked: false },
    { id: 'portfolioSettings', name: 'Portfolio Settings', checked: false },
    { id: 'portfolioUsers', name: 'Portfolio Users', checked: false },
    { id: 'businessSettings', name: 'Business Settings', checked: false },
    { id: 'businessUsers', name: 'Business Users', checked: false },
    { id: 'campaigns', name: 'Campaigns', checked: false },
    { id: 'budget', name: 'Budget', checked: false },
    { id: 'billing', name: 'Billing', checked: false },
    { id: 'leads', name: 'Leads', checked: false }
  ];
  
  // For Primary Owner and Owner, all permissions are checked except viewOnly
  if (role === 'Primary Owner' || role === 'Owner') {
    return basePermissions.map(p => {
      return {
        ...p,
        checked: p.id !== 'viewOnly' // All permissions except viewOnly
      };
    });
  }
  
  // For Manager, grant some default permissions
  if (role === 'Manager') {
    return basePermissions.map(p => {
      if (p.id === 'viewOnly') return { ...p, checked: false };
      if (['portfolioSettings', 'portfolioUsers', 'businessSettings', 'campaigns', 'leads'].includes(p.id)) {
        return { ...p, checked: true };
      }
      return { ...p, checked: false };
    });
  }
  
  // Default for other roles: only viewOnly is checked
  return basePermissions.map(p => {
    return {
      ...p,
      checked: p.id === 'viewOnly'
    };
  });
};

/**
 * Check if member can manage business users
 */
export const canManageBusinessUsers = (
  businessMembers: Member[],
  portfolioMembers: Member[] | null,
  source: 'portfolio' | 'business'
): boolean => {
  // In portfolio context, primary owners and owners can always manage business users
  if (source === 'portfolio') {
    const isCurrentUserPortfolioPrimaryOwnerOrOwner = businessMembers.some(member => 
      member.isCurrentUser && (member.role === 'Primary Owner' || member.role === 'Owner')
    );
    
    if (isCurrentUserPortfolioPrimaryOwnerOrOwner) {
      return true;
    }
  }
  
  // In business context, check business member permissions
  const currentMember = businessMembers.find(member => member.isCurrentUser);
  if (!currentMember) return false;
  
  // Primary owners and owners can always manage users
  if (currentMember.role === 'Primary Owner' || currentMember.role === 'Owner') {
    return true;
  }
  
  // Check if member has businessUsers permission
  const hasBusinessUsersPermission = currentMember.permissions?.some(p => 
    p.id === 'businessUsers' && p.checked
  );
  
  return Boolean(hasBusinessUsersPermission);
};

/**
 * Get effective permission for actions based on role and context
 */
export const getEffectivePermission = (
  members: Member[],
  portfolioMembers: Member[] | null,
  source: 'portfolio' | 'business'
): boolean => {
  // First check if the current user is a member
  const currentMember = members.find(member => member.isCurrentUser);
  if (!currentMember) return false;
  
  // Primary Owners and Owners have all permissions
  if (currentMember.role === 'Primary Owner' || currentMember.role === 'Owner') {
    return true;
  }
  
  // For portfolio context
  if (source === 'portfolio') {
    const hasPortfolioUsersPermission = currentMember.permissions?.some(p => 
      p.id === 'portfolioUsers' && p.checked
    );
    
    return Boolean(hasPortfolioUsersPermission);
  }
  
  // For business context
  if (source === 'business') {
    // Check if user has business users permission
    const hasBusinessUsersPermission = currentMember.permissions?.some(p => 
      p.id === 'businessUsers' && p.checked
    );
    
    // If they have direct permission, allow it
    if (hasBusinessUsersPermission) {
      return true;
    }
    
    // If portfolio members were provided, check portfolio-level permissions
    if (portfolioMembers && portfolioMembers.length > 0) {
      const portfolioCurrentMember = portfolioMembers.find(member => member.isCurrentUser);
      if (portfolioCurrentMember && 
          (portfolioCurrentMember.role === 'Primary Owner' || 
           portfolioCurrentMember.role === 'Owner')) {
        return true;
      }
    }
  }
  
  return false;
};
